// client/src/components/media/NativeCameraCapture.jsx
// Component for capturing images and videos using the device's native camera application
// This uses HTML5 file input with capture attribute, which triggers native camera on mobile devices

import React, { useState, useRef, useEffect } from 'react';
import { FaCamera, FaVideo, FaTimes, FaCheckCircle, FaRedo } from 'react-icons/fa';
import './NativeCameraCapture.css';

/**
 * NativeCameraCapture Component
 * 
 * Uses the device's native camera application to capture images and videos
 * 
 * Props:
 * @param {Function} onCapture - Callback when media is captured: (files: File[]) => void
 * @param {Function} onClose - Callback when component is closed
 * @param {String} mode - 'photo' | 'video' | 'both' - What type of media to capture (default: 'both')
 * @param {Boolean} multiple - Allow multiple captures (default: false)
 * @param {Number} maxFiles - Maximum number of files to capture (default: 10)
 * @param {Number} maxImageSizeMB - Maximum image size in MB (default: 10)
 * @param {Number} maxVideoSizeMB - Maximum video size in MB (default: 100)
 */
const NativeCameraCapture = ({
  onCapture,
  onClose,
  mode = 'both',
  multiple = false,
  maxFiles = 10,
  maxImageSizeMB = 10,
  maxVideoSizeMB = 100
}) => {
  // State for captured media files
  const [capturedFiles, setCapturedFiles] = useState([]);
  // State for preview URLs (for display purposes)
  const [previewUrls, setPreviewUrls] = useState([]);
  // State for error messages
  const [error, setError] = useState('');
  // State for permission status
  const [permissionDenied, setPermissionDenied] = useState(false);
  // State to track if user has started capture process
  const [captureInProgress, setCaptureInProgress] = useState(false);

  // Refs for file inputs (hidden)
  const photoInputRef = useRef(null);
  const videoInputRef = useRef(null);

  /**
   * Determine the accept attribute based on mode
   */
  const getAcceptAttribute = () => {
    switch (mode) {
      case 'photo':
        return 'image/*';
      case 'video':
        return 'video/*';
      case 'both':
      default:
        return 'image/*,video/*';
    }
  };

  /**
   * Handle file selection from native camera/gallery
   * This is called when user completes capture or selects from gallery
   */
  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files || []);
    
    if (!files.length) {
      setCaptureInProgress(false);
      return;
    }

    setError('');
    const validFiles = [];
    const newPreviewUrls = [];
    let errorMessages = [];

    // Check if we'll exceed max files
    const totalFiles = capturedFiles.length + files.length;
    if (totalFiles > maxFiles) {
      errorMessages.push(`Maximum ${maxFiles} files allowed. Only processing first ${maxFiles - capturedFiles.length} files.`);
    }

    // Process each file
    const filesToProcess = files.slice(0, maxFiles - capturedFiles.length);
    
    filesToProcess.forEach((file) => {
      const isImage = file.type.startsWith('image/');
      const isVideo = file.type.startsWith('video/');
      const sizeMB = file.size / (1024 * 1024);

      // Validate file type based on mode
      if (mode === 'photo' && !isImage) {
        errorMessages.push(`${file.name}: Only images are allowed in photo mode`);
        return;
      }
      if (mode === 'video' && !isVideo) {
        errorMessages.push(`${file.name}: Only videos are allowed in video mode`);
        return;
      }
      if (!isImage && !isVideo) {
        errorMessages.push(`${file.name}: Invalid file type`);
        return;
      }

      // Validate file size
      if (isImage && sizeMB > maxImageSizeMB) {
        errorMessages.push(`${file.name}: Image too large (max ${maxImageSizeMB}MB)`);
        return;
      }
      if (isVideo && sizeMB > maxVideoSizeMB) {
        errorMessages.push(`${file.name}: Video too large (max ${maxVideoSizeMB}MB)`);
        return;
      }

      // File is valid, add to arrays
      validFiles.push(file);
      
      // Create preview URL
      const previewUrl = URL.createObjectURL(file);
      newPreviewUrls.push({
        url: previewUrl,
        type: isImage ? 'image' : 'video',
        name: file.name,
        size: file.size
      });
    });

    // Update state with new files
    if (validFiles.length > 0) {
      if (multiple) {
        setCapturedFiles([...capturedFiles, ...validFiles]);
        setPreviewUrls([...previewUrls, ...newPreviewUrls]);
      } else {
        // For single capture, replace existing
        // Revoke old preview URLs to prevent memory leaks
        previewUrls.forEach(preview => URL.revokeObjectURL(preview.url));
        setCapturedFiles(validFiles);
        setPreviewUrls(newPreviewUrls);
      }
    }

    // Show any error messages
    if (errorMessages.length > 0) {
      setError(errorMessages.join('. '));
    }

    setCaptureInProgress(false);

    // Reset input value so same file can be selected again
    event.target.value = '';
  };

  /**
   * Trigger the appropriate camera based on type
   */
  const triggerCamera = (type) => {
    setError('');
    setPermissionDenied(false);
    setCaptureInProgress(true);

    try {
      if (type === 'photo' && photoInputRef.current) {
        photoInputRef.current.click();
      } else if (type === 'video' && videoInputRef.current) {
        videoInputRef.current.click();
      }
    } catch (err) {
      console.error('Error triggering camera:', err);
      setError('Failed to open camera. Please try again.');
      setCaptureInProgress(false);
    }
  };

  /**
   * Remove a specific captured file
   */
  const removeFile = (index) => {
    // Revoke the object URL to prevent memory leaks
    if (previewUrls[index]?.url) {
      URL.revokeObjectURL(previewUrls[index].url);
    }

    setCapturedFiles(capturedFiles.filter((_, i) => i !== index));
    setPreviewUrls(previewUrls.filter((_, i) => i !== index));
  };

  /**
   * Handle confirm - pass captured files to parent
   */
  const handleConfirm = () => {
    if (capturedFiles.length === 0) {
      setError('Please capture at least one photo or video');
      return;
    }

    onCapture(capturedFiles);
  };

  /**
   * Handle retry - clear all captures
   */
  const handleRetry = () => {
    // Revoke all object URLs to prevent memory leaks
    previewUrls.forEach(preview => URL.revokeObjectURL(preview.url));
    
    setCapturedFiles([]);
    setPreviewUrls([]);
    setError('');
    setPermissionDenied(false);
  };

  /**
   * Cleanup on unmount - revoke all object URLs
   */
  useEffect(() => {
    return () => {
      previewUrls.forEach(preview => {
        if (preview?.url) {
          URL.revokeObjectURL(preview.url);
        }
      });
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Handle permission errors
   * Note: Permission prompts are handled automatically by the browser
   * when the file input is triggered
   */
  const handlePermissionError = () => {
    setPermissionDenied(true);
    setError('Camera access was denied. Please enable camera permissions in your device settings and try again.');
    setCaptureInProgress(false);
  };

  return (
    <div className="native-camera-capture">
      <div className="native-camera-overlay" onClick={onClose} />
      
      <div className="native-camera-content">
        {/* Header */}
        <div className="native-camera-header">
          <h3 className="native-camera-title">
            {mode === 'photo' ? 'Capture Photo' : mode === 'video' ? 'Capture Video' : 'Capture Media'}
          </h3>
          <button 
            className="native-camera-close-btn"
            onClick={onClose}
            aria-label="Close"
          >
            <FaTimes />
          </button>
        </div>

        {/* Instructions */}
        {capturedFiles.length === 0 && !captureInProgress && (
          <div className="native-camera-instructions">
            <p>
              {mode === 'both' 
                ? 'Choose whether to capture a photo or video using your device\'s camera'
                : mode === 'photo'
                ? 'Tap the button below to open your device\'s camera and take a photo'
                : 'Tap the button below to open your device\'s camera and record a video'
              }
            </p>
            {multiple && (
              <p className="text-muted">You can capture up to {maxFiles} files</p>
            )}
          </div>
        )}

        {/* Error Display */}
        {error && (
          <div className="native-camera-error">
            <p>{error}</p>
            {permissionDenied && (
              <div className="native-camera-permission-help">
                <p><strong>To enable camera access:</strong></p>
                <ul>
                  <li>Go to your device Settings</li>
                  <li>Find this browser in your apps list</li>
                  <li>Enable Camera permissions</li>
                  <li>Return here and try again</li>
                </ul>
              </div>
            )}
          </div>
        )}

        {/* Preview Area */}
        {previewUrls.length > 0 && (
          <div className="native-camera-preview-area">
            <h4>Captured Media ({capturedFiles.length})</h4>
            <div className="native-camera-preview-grid">
              {previewUrls.map((preview, index) => (
                <div key={index} className="native-camera-preview-item">
                  {preview.type === 'image' ? (
                    <img 
                      src={preview.url} 
                      alt={`Capture ${index + 1}`}
                      className="native-camera-preview-image"
                    />
                  ) : (
                    <video 
                      src={preview.url}
                      controls
                      playsInline
                      className="native-camera-preview-video"
                    />
                  )}
                  <button
                    className="native-camera-remove-btn"
                    onClick={() => removeFile(index)}
                    aria-label="Remove"
                  >
                    <FaTimes />
                  </button>
                  <div className="native-camera-preview-info">
                    <span className="file-type">{preview.type}</span>
                    <span className="file-size">
                      {(preview.size / (1024 * 1024)).toFixed(2)} MB
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="native-camera-actions">
          {capturedFiles.length === 0 || (multiple && capturedFiles.length < maxFiles) ? (
            <div className="native-camera-capture-buttons">
              {/* Photo Capture Button */}
              {(mode === 'photo' || mode === 'both') && (
                <button
                  className="native-camera-btn native-camera-btn-photo"
                  onClick={() => triggerCamera('photo')}
                  disabled={captureInProgress}
                >
                  <FaCamera />
                  <span>Take Photo</span>
                </button>
              )}

              {/* Video Capture Button */}
              {(mode === 'video' || mode === 'both') && (
                <button
                  className="native-camera-btn native-camera-btn-video"
                  onClick={() => triggerCamera('video')}
                  disabled={captureInProgress}
                >
                  <FaVideo />
                  <span>Record Video</span>
                </button>
              )}
            </div>
          ) : null}

          {/* Confirm/Retry Buttons (shown when files are captured) */}
          {capturedFiles.length > 0 && (
            <div className="native-camera-confirm-buttons">
              <button
                className="native-camera-btn native-camera-btn-retry"
                onClick={handleRetry}
              >
                <FaRedo />
                <span>Retry</span>
              </button>
              <button
                className="native-camera-btn native-camera-btn-confirm"
                onClick={handleConfirm}
              >
                <FaCheckCircle />
                <span>Use {capturedFiles.length} {capturedFiles.length === 1 ? 'File' : 'Files'}</span>
              </button>
            </div>
          )}

          {/* Additional captures (for multiple mode) */}
          {multiple && capturedFiles.length > 0 && capturedFiles.length < maxFiles && (
            <div className="native-camera-add-more">
              <p className="text-muted">
                {maxFiles - capturedFiles.length} more {maxFiles - capturedFiles.length === 1 ? 'file' : 'files'} available
              </p>
            </div>
          )}
        </div>

        {/* Hidden file inputs - these trigger the native camera */}
        {/* Photo input */}
        {(mode === 'photo' || mode === 'both') && (
          <input
            ref={photoInputRef}
            type="file"
            accept="image/*"
            capture="environment" // Use rear camera by default on mobile
            multiple={multiple}
            onChange={handleFileSelect}
            style={{ display: 'none' }}
            aria-label="Capture photo"
          />
        )}

        {/* Video input */}
        {(mode === 'video' || mode === 'both') && (
          <input
            ref={videoInputRef}
            type="file"
            accept="video/*"
            capture="environment" // Use rear camera by default on mobile
            multiple={multiple}
            onChange={handleFileSelect}
            style={{ display: 'none' }}
            aria-label="Capture video"
          />
        )}
      </div>
    </div>
  );
};

export default NativeCameraCapture;